import { motion } from "framer-motion";
import SectionAtmosphere from "@/components/SectionAtmosphere";
import atmosphereManifesto from "@/assets/atmosphere-manifesto.jpg";
import { lqipFor } from "@/generated/atmospheric-lqip";
import { letterRise, riseFade, splitChars } from "@/lib/motion";

/**
 * Editorial Manifesto break.
 *
 * A single bold typographic statement on a deep midnight panel, the
 * "intermission" that punctuates the long marketing scroll. Borrowed from
 * Stripe Press / The Atlantic feature pieces: when every section is a card
 * grid, the page reads transactional; one full-bleed quiet panel restores
 * the editorial cadence and re-anchors the brand voice.
 *
 * Design rules followed:
 *  - `tone="dark"` on midnight primary, atmospheric photo at 0.35 opacity.
 *  - Single Instrument Serif italic line. No card. No CTA. No icon.
 *  - Eyebrow + tiny attribution beneath, low-tracking small caps.
 *  - Letter-rise stagger reuses the shared motion vocabulary so this feels
 *    like the SAME film as the Hero.
 */
const Manifesto = () => {
  const line1 = "Fatigue is not";
  const line2 = "a willpower problem.";
  const line3 = "It's a pattern. ";
  const line4 = "and patterns can be planned around.";
  const ariaLabel = [line1, line2, line3, line4].join(" ");

  return (
    <SectionAtmosphere
      ariaLabelledBy="manifesto-heading"
      src={atmosphereManifesto}
      optimizedBase="/opt/atmosphere-manifesto"
      lqip={lqipFor(atmosphereManifesto)}
      origin="center"
      intensity={0.38}
      glow="none"
      tone="dark"
      className="py-32 sm:py-44"
    >
      <div className="container mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-4xl mx-auto text-center space-y-10">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 0.8 }}
            className="eyebrow justify-center mx-auto text-primary-foreground/75"
          >
            A small manifesto
          </motion.div>

          <motion.h2
            id="manifesto-heading"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-15% 0px" }}
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.018, delayChildren: 0.1 } },
            }}
            className="font-display text-primary-foreground text-display-2xl leading-[1.05]"
            aria-label={ariaLabel}
          >
            <span className="block">
              {splitChars(line1).map(({ char, key }) => (
                <motion.span key={key} variants={letterRise} className="inline-block">
                  {char}
                </motion.span>
              ))}
            </span>
            <span className="block italic text-primary-foreground/85">
              {splitChars(line2).map(({ char, key }) => (
                <motion.span key={`a-${key}`} variants={letterRise} className="inline-block">
                  {char}
                </motion.span>
              ))}
            </span>
            <span className="block mt-3">
              {splitChars(line3).map(({ char, key }) => (
                <motion.span key={`b-${key}`} variants={letterRise} className="inline-block">
                  {char}
                </motion.span>
              ))}
            </span>
            <span className="block italic text-primary-foreground/85">
              {splitChars(line4).map(({ char, key }) => (
                <motion.span key={`c-${key}`} variants={letterRise} className="inline-block">
                  {char}
                </motion.span>
              ))}
            </span>
          </motion.h2>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-15% 0px" }}
            variants={riseFade}
            transition={{ delay: 0.6 }}
            className="space-y-3"
          >
            <div
              aria-hidden="true"
              className="mx-auto h-px w-12 bg-primary-foreground/30"
            />
            <p className="text-[11px] tracking-[0.32em] uppercase text-primary-foreground/55">
              The Myelina Health principle
            </p>
          </motion.div>
        </div>
      </div>
    </SectionAtmosphere>
  );
};

export default Manifesto;
